import type { ComponentProps } from 'react'
import { useEffect, useState } from 'react'

import type { VariantProps } from '@joyful/utils'
import { formatRelativeTimestamp, formatTimestamp, tv } from '@joyful/utils'

import { Tooltip } from './Tooltip'

export const relativeTimestampVariants = tv({
  base: null,

  defaultVariants: {
    size: 'inherit',
    tone: 'default',
    weight: 'normal',
  },

  variants: {
    size: {
      inherit: {},
      sm: 'text-sm',
      xs: 'text-xs',
    },

    tone: {
      default: 'text-tremor-content',
      subtle: 'text-tremor-content-subtle',
    },

    weight: {
      normal: {},
      semibold: 'font-semibold',
    },
  },
})

export interface RelativeTimestampProps
  extends
    Omit<ComponentProps<'span'>, 'className' | 'children'>,
    VariantProps<typeof relativeTimestampVariants> {
  timestamp: string
}

export function RelativeTimestamp({
  size,
  timestamp,
  tone,
  weight,
  ...props
}: RelativeTimestampProps) {
  const [, _rerender] = useState()
  useEffect(() => {
    const interval = setInterval(() => {
      _rerender(undefined)
    }, 60_000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <Tooltip content={formatTimestamp(timestamp)}>
      <span
        className={relativeTimestampVariants({ size, tone, weight })}
        {...props}
      >
        {formatRelativeTimestamp(timestamp)}
      </span>
    </Tooltip>
  )
}
